import { trackSevenDayStreak } from "@/lib/analytics/google-ads-tracking";
import { createLogger } from "@/lib/logger";
import { StreakData, StreakDisplay, getStreakDisplay } from "@/lib/streakSystem";
import { supabase } from "@/lib/supabaseClient";
import { useEffect, useRef, useState } from "react";

const logger = createLogger("streak");

async function getStreakAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;
}

interface UseStreakResult {
  streakData: StreakData | null;
  streakDisplay: StreakDisplay | null;
  loading: boolean;
  error: string | null;
  updateStreak: () => Promise<void>;
  refreshStreak: () => Promise<void>;
}

/**
 * Hook to manage user streak data
 * @param userId - The user's UUID from Supabase auth
 */
export function useStreak(userId: string | null): UseStreakResult {
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [streakDisplay, setStreakDisplay] = useState<StreakDisplay | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Track which milestone streaks have been reported (to avoid duplicate conversions)
  const trackedMilestonesRef = useRef<Set<number>>(new Set());

  /**
   * Fetch current streak data from API
   */
  const fetchStreak = async () => {
    if (!userId) {
      setStreakData(null);
      setStreakDisplay(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = await getStreakAuthHeaders();
      const response = await fetch("/api/streak", {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch streak: ${response.statusText}`);
      }

      const data = (await response.json()) as any;

      if (data.success && data.streak) {
        setStreakData(data.streak);
        setStreakDisplay(getStreakDisplay(data.streak));
      } else {
        throw new Error(data.error || "Failed to get streak data");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      setError(errorMessage);
      logger.error("Error fetching streak", { error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get user's local date in YYYY-MM-DD format
   * This ensures streak is calculated based on user's timezone, not server time
   */
  const getUserLocalDate = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /**
   * Update streak (call when user performs an activity)
   */
  const updateStreak = async () => {
    if (!userId) return;

    try {
      // Send user's local date to ensure timezone-correct streak tracking
      const userLocalDate = getUserLocalDate();
      const authHeaders = await getStreakAuthHeaders();

      const response = await fetch("/api/streak", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ userLocalDate }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update streak: ${response.statusText}`);
      }

      const data = (await response.json()) as any;

      if (data.success && data.streak) {
        setStreakData(data.streak);
        setStreakDisplay(getStreakDisplay(data.streak));

        // Track milestone conversions (7-day, 30-day, 90-day streaks)
        if (data.hitMilestone && data.milestoneDays) {
          const milestone = data.milestoneDays;

          // Only track if we haven't already tracked this milestone
          if (!trackedMilestonesRef.current.has(milestone)) {
            trackedMilestonesRef.current.add(milestone);

            // Track the appropriate conversion based on milestone
            if (milestone === 7) {
              trackSevenDayStreak({ streak_days: 7 });
              logger.info("7-day streak conversion tracked");
            }
            // Future: Add 30-day, 90-day tracking here if needed
            else if (milestone === 30) {
              logger.info("30-day milestone reached");
              // trackThirtyDayStreak({ streak_days: 30 });
            } else if (milestone === 90) {
              logger.info("90-day milestone reached");
              // trackNinetyDayStreak({ streak_days: 90 });
            }
          }
        }

        // Log streak update
        logger.debug("Streak updated", {
          currentStreak: data.streak.currentStreak,
          longestStreak: data.streak.longestStreak,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      logger.error("Error updating streak", { error: errorMessage });
      // Don't set error state here - streak update is not critical
    }
  };

  /**
   * Manually refresh streak data
   */
  const refreshStreak = async () => {
    await fetchStreak();
  };

  // Fetch streak on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchStreak();
    }
  }, [userId]);

  return {
    streakData,
    streakDisplay,
    loading,
    error,
    updateStreak,
    refreshStreak,
  };
}
